import React, { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../styles/AiResearch.css';
import '../components/pdf/SplitScreenViewer.css';
import PdfHighlighter from './pdf/PdfHighlighter';
import { api } from '../auth/apiClient';

const buildSessionKey = (companyId, basis) => `ai-thread:${companyId}:${basis}`;
const GENERATE_TIMEOUT_MS = 180000;

const AiResearchTab = ({ companyId, basis }) => {
    const [question, setQuestion] = useState('');
    const [thread, setThread] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const listRef = useRef(null);

    // Split-screen PDF state
    const [selectedCitation, setSelectedCitation] = useState(null);

    // Memory state — loaded from the server-side thread store
    const [memoryMeta, setMemoryMeta] = useState({ totalTurns: 0, hasSummary: false, clearingMemory: false });

    // Load history from backend on mount; fall back to sessionStorage while loading
    useEffect(() => {
        if (!companyId || !basis) return;
        let cancelled = false;

        const loadHistory = async () => {
            const saved = sessionStorage.getItem(buildSessionKey(companyId, basis));
            if (saved) {
                try { setThread(JSON.parse(saved)); } catch (e) { /* ignore */ }
            }

            try {
                const [data, tmeta] = await Promise.all([
                    api.get('/ai/research', {
                        params: { companyId, basis, tab: 'aiResearch', limit: 20 },
                    }),
                    api.get('/ai/research/thread', {
                        params: { companyId, basis },
                    }),
                ]);

                const items = Array.isArray(data?.data) ? data.data : [];
                if (!cancelled && items.length > 0) {
                    setThread((prev) => {
                        const backendIds = new Set(items.map((i) => i._id).filter(Boolean));
                        const localOnly = prev.filter((i) => !i._id || !backendIds.has(i._id));
                        const merged = [...localOnly, ...items];
                        sessionStorage.setItem(buildSessionKey(companyId, basis), JSON.stringify(merged));
                        return merged;
                    });
                } else if (!cancelled && items.length === 0 && !saved) {
                    setThread([]);
                }

                if (!cancelled) {
                    setMemoryMeta((prev) => ({
                        ...prev,
                        totalTurns: tmeta.totalTurns || 0,
                        hasSummary: tmeta.hasSummary || false,
                    }));
                }
            } catch (e) {
                // Backend unavailable – silently keep sessionStorage version
            }
        };

        loadHistory();
        return () => { cancelled = true; };
    }, [companyId, basis]);

    const persistThread = (next) => {
        setThread(next);
        sessionStorage.setItem(buildSessionKey(companyId, basis), JSON.stringify(next));
    };

    const handleAsk = async () => {
        if (!question.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const data = await api.post('/ai/research', {
                companyId,
                basis,
                tab: 'aiResearch',
                question: question.trim(),
            }, { timeout: GENERATE_TIMEOUT_MS });
            if (!data?.data) {
                throw new Error(data?.message || 'Failed to fetch AI insight');
            }
            const item = { ...data.data, source: data.source || 'generated', question: question.trim() };
            const next = [item, ...thread];
            persistThread(next);
            setQuestion('');

            const memCtx = data?.metadata?.memoryContext;
            if (memCtx) {
                setMemoryMeta((prev) => ({
                    ...prev,
                    totalTurns: memCtx.sessionTurns ?? prev.totalTurns + 1,
                    hasSummary: memCtx.hasSummary ?? prev.hasSummary,
                }));
            } else {
                setMemoryMeta((prev) => ({ ...prev, totalTurns: prev.totalTurns + 1 }));
            }

            setTimeout(() => {
                if (listRef.current) {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                }
            }, 100);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to ask Finlytics AI');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        persistThread([]);
        setSelectedCitation(null);
    };

    const handleClearMemory = async () => {
        if (memoryMeta.clearingMemory) return;
        setMemoryMeta((prev) => ({ ...prev, clearingMemory: true }));
        try {
            await api.delete('/ai/research/thread', {
                params: { companyId, basis },
            });
            setMemoryMeta({ totalTurns: 0, hasSummary: false, clearingMemory: false });
        } catch (e) {
            setMemoryMeta((prev) => ({ ...prev, clearingMemory: false }));
        }
    };

    return (
        <div className="split-screen-container" style={{ height: 'calc(100vh - 150px)', border: 'none', boxShadow: 'none' }}>
            {/* LEFT PANE: AI Chat Thread */}
            <div className="split-screen-left" style={{ position: 'relative', minWidth: '400px', maxWidth: '550px' }}>
                <div className="ai-thread" ref={listRef} style={{ paddingBottom: '140px', height: '100%', overflowY: 'auto', backgroundColor: '#ffffff' }}>
                    {!thread.length && (
                        <div className="ai-empty">
                            <div className="ai-empty-title">No AI answers yet</div>
                            <div className="ai-empty-sub">Ask a question to generate your first insight for this session.</div>
                        </div>
                    )}

                    {[...thread].reverse().map((item, idx) => (
                        <div className="ai-bubble" key={item._id || idx} style={{ margin: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <div className="ai-question" style={{ marginBottom: '12px', fontWeight: 'bold', color: '#1e293b' }}>
                                <span style={{ color: '#3b82f6', marginRight: '8px' }}>Q:</span>
                                {item.questionOriginal || item.question || 'Question'}
                            </div>

                            <div className="ai-sections">
                                {(item.answerSections || []).map((section) => {
                                    if (section.title === 'Executive Summary' || section.key === 'executive_summary') return null;
                                    return (
                                        <div className="ai-section bubble-section" key={section.key} style={{ marginTop: '12px' }}>
                                            <div className="ai-section-title" style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '4px' }}>{section.title}</div>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {section.content || ''}
                                            </ReactMarkdown>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Render Citations for verification */}
                            {(item.sources || item.citations) && (
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                                    <span style={{ fontSize: '0.8rem', color: '#64748b', width: '100%' }}>Source Citations (Click to verify)</span>
                                    {(item.sources || item.citations).map((cit, i) => (
                                        <button 
                                            key={i} 
                                            onClick={() => setSelectedCitation(cit)}
                                            style={{
                                                padding: '4px 10px', fontSize: '0.8rem', borderRadius: '16px', cursor: 'pointer',
                                                backgroundColor: selectedCitation?.id === cit.id ? '#3b82f6' : '#e2e8f0',
                                                color: selectedCitation?.id === cit.id ? '#ffffff' : '#475569',
                                                border: 'none', transition: 'all 0.2s'
                                            }}
                                        >
                                            📄 {cit.docType || 'Doc'} (Pg {cit.pageStart || cit.pageNumber || 1})
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* DOCKED INPUT PANEL */}
                <div className="ai-input-panel docked" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', padding: '16px', borderTop: '1px solid #e2e8f0' }}>
                    <div className="ask-row" style={{ display: 'flex', gap: '12px' }}>
                        <textarea
                            style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'none' }}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Ask Finlytics AI..."
                            rows={1}
                        />
                        <button
                            style={{ padding: '0 20px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={handleAsk}
                            disabled={loading || !question.trim()}
                        >
                            {loading ? 'Thinking…' : 'Ask'}
                        </button>
                    </div>
                    {error && <div style={{ color: 'red', marginTop: '8px', fontSize: '0.85rem' }}>{error}</div>}
                    <div className="ai-footer-row" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '0.8rem' }}>
                        <button style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }} onClick={handleClear} disabled={loading || !thread.length}>
                            Clear session
                        </button>
                        {memoryMeta.totalTurns > 0 && (
                            <span style={{ color: '#64748b' }}>
                                {memoryMeta.hasSummary ? '🧠 Summary + ' : '🧠 '}{memoryMeta.totalTurns} turns in memory
                                <button style={{ marginLeft: '12px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }} onClick={handleClearMemory}>
                                    Clear memory
                                </button>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* RIGHT PANE: PDF Split Screen Verification */}
            <div className="split-screen-right">
                <div className="pdf-pane-header">
                    <h3>Source Verification</h3>
                    {selectedCitation && (
                        <span className="verification-badge">
                            Active Highlight: {selectedCitation.companyId || companyId} - Pg {selectedCitation.pageStart || selectedCitation.pageNumber}
                        </span>
                    )}
                </div>
                
                <div className="pdf-pane-body">
                    {selectedCitation && selectedCitation.blobUri ? (
                        <PdfHighlighter 
                            fileUrl={selectedCitation.blobUri}
                            pageNumber={selectedCitation.pageStart || selectedCitation.pageNumber || 1}
                            bbox={selectedCitation.metadata?.bbox || selectedCitation.bbox || null}
                        />
                    ) : (
                        <div className="empty-pdf-state">
                            <div className="pdf-placeholder-icon">📄</div>
                            <p>Select a citation chip from an AI answer to load and verify the source PDF.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AiResearchTab;
